import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "types";

import {
  Switch,
  CircularProgress,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Alert,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import ClearIcon from "@mui/icons-material/Clear";
import NotificationsIcon from "@mui/icons-material/Notifications";
import EmailIcon from "@mui/icons-material/Email";
import SmsIcon from "@mui/icons-material/Sms";
import EditIconBtn from "app/Components/Buttons/IconBtns/EditIconBtn";
import SaveIconBtn from "app/Components/Buttons/IconBtns/SaveIconBtn";
import CancelIconBtn from "app/Components/Buttons/IconBtns/CancelIconBtn";

import { db } from "config/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import UpdatePhoneNumber from "./UpdatePhoneNumber.jsx";

interface NotificationPreferences {
  newMessage?: boolean;
  requestAccepted?: boolean;
  requestDeclined?: boolean;
  confirmationNeeded?: boolean;
  confirmationAccepted?: boolean;
  confirmationDeclined?: boolean;
}

interface HostPreferences {
  requestReceived?: boolean;
  playerConfirmed?: boolean;
  playerDeclined?: boolean;
}



function UserPreferences() {
  const user = useSelector((state: RootState) => state.user.user);
  const claims = useSelector((state: RootState) => state.user.token);

  const [loading, setLoading] = useState(true);
  const [userEmailPreferences, setUserEmailPreferences] =
    useState<NotificationPreferences>({});
  const [userTextPreferences, setUserTextPreferences] =
    useState<NotificationPreferences>({});
  const [hostEmailPreferences, setHostEmailPreferences] =
    useState<HostPreferences>({});
  const [hostTextPreferences, setHostTextPreferences] =
    useState<HostPreferences>({});
  
  // Original values to compare against for changes detection
  const [originalUserEmailPreferences, setOriginalUserEmailPreferences] =
    useState<NotificationPreferences>({});
  const [originalUserTextPreferences, setOriginalUserTextPreferences] =
    useState<NotificationPreferences>({});
  const [originalHostEmailPreferences, setOriginalHostEmailPreferences] =
    useState<HostPreferences>({});
  const [originalHostTextPreferences, setOriginalHostTextPreferences] =
    useState<HostPreferences>({});
  
  const [saveError, setSaveError] = useState<string | null>(null);

  // Check if any preferences have changed
  const hasChanges = () => {
    return (
      JSON.stringify(userEmailPreferences) !== JSON.stringify(originalUserEmailPreferences) ||
      JSON.stringify(userTextPreferences) !== JSON.stringify(originalUserTextPreferences) ||
      JSON.stringify(hostEmailPreferences) !== JSON.stringify(originalHostEmailPreferences) ||
      JSON.stringify(hostTextPreferences) !== JSON.stringify(originalHostTextPreferences)
    );
  };

  useEffect(() => {
    (async function IIFE() {
      await getUserPreferences();
      await getHostPreferences();
      setLoading(false);
    })();
  }, [claims]);

  const handleChange = (
    key: keyof NotificationPreferences,
    checked: boolean,
  ) => {
    setUserEmailPreferences({ ...userEmailPreferences, [key]: checked });
  };

  const handleTextChange = (
    key: keyof NotificationPreferences,
    checked: boolean,
  ) => {
    if (user.phoneNumber) {
      setUserTextPreferences({ ...userTextPreferences, [key]: checked });
    }
  };

  const handleHostChange = (key: keyof HostPreferences, checked: boolean) => {
    setHostEmailPreferences({ ...hostEmailPreferences, [key]: checked });
  };

  const handleHostTextChange = (
    key: keyof HostPreferences,
    checked: boolean,
  ) => {
    if (user.phoneNumber) {
      setHostTextPreferences({ ...hostTextPreferences, [key]: checked });
    }
  };

  const handleAllEmailOpt = (checked: boolean) => {
    const userKeys = Object.keys(
      userEmailPreferences,
    ) as (keyof NotificationPreferences)[];
    const updatedUserPreferences: NotificationPreferences = {};

    userKeys.forEach((key) => {
      updatedUserPreferences[key] = checked;
    });
    setUserEmailPreferences(updatedUserPreferences);

    if (hostEmailPreferences && Object.keys(hostEmailPreferences).length > 0) {
      const hostKeys = Object.keys(
        hostEmailPreferences,
      ) as (keyof HostPreferences)[];
      const updatedHostPreferences: HostPreferences = {};

      hostKeys.forEach((key) => {
        updatedHostPreferences[key] = checked;
      });
      setHostEmailPreferences(updatedHostPreferences);
    }
  };

  const getUserPreferences = async () => {
    const userDocRef = doc(db, "users", user.uid, "data", "metadata");
    const docSnap = await getDoc(userDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const emailPrefs = data.emailNotifications || {};
      const textPrefs = data.textNotifications || {};
      
      setUserEmailPreferences(emailPrefs);
      setUserTextPreferences(textPrefs);
      setOriginalUserEmailPreferences(emailPrefs);
      setOriginalUserTextPreferences(textPrefs);
    }
  };

  const getHostPreferences = async () => {
    if (claims && claims.isHost) {
      const hostDocRef = doc(db, "hosts", user.uid);
      const docSnap = await getDoc(hostDocRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const emailPrefs = data.emailNotifications || {};
        const textPrefs = data.textNotifications || {};
        
        setHostEmailPreferences(emailPrefs);
        setHostTextPreferences(textPrefs);
        setOriginalHostEmailPreferences(emailPrefs);
        setOriginalHostTextPreferences(textPrefs);
      }
    }
  };

  const UserPrefMap = {
    newMessage: "New Message Received",
    requestAccepted: "Request Accepted by Host",
    requestDeclined: "Request Declined by Host",
    confirmationNeeded: "Seat Confirmation Needed",
    confirmationAccepted: "Seat Confirmed By Host ",
    confirmationDeclined: "Seat Declined By Host",
  };

  const HostPrefMap = {
    requestReceived: "New Game Request",
    playerConfirmed: "Player Confirmed Seat at Table",
    playerDeclined: "Player Declined Seat at Table",
  };

  const savePreferences = async () => {
    try {
      setSaveError(null);

      await updateDoc(doc(db, "users", user.uid, "data", "metadata"), {
        emailNotifications: userEmailPreferences,
        textNotifications: userTextPreferences,
      });

      if (claims && claims.isHost) {
        await updateDoc(doc(db, "hosts", user.uid), {
          emailNotifications: hostEmailPreferences,
          textNotifications: hostTextPreferences,
        });
      }

      // Update original values to reflect the saved state
      setOriginalUserEmailPreferences(userEmailPreferences);
      setOriginalUserTextPreferences(userTextPreferences);
      setOriginalHostEmailPreferences(hostEmailPreferences);
      setOriginalHostTextPreferences(hostTextPreferences);
      
    } catch (error) {
      console.error("Error saving preferences:", error);
      setSaveError("Failed to save preferences. Please try again.");
    }
  };

  const resetPreferences = async () => {
    setSaveError(null);
    await getUserPreferences();
    await getHostPreferences();
  };

  const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    "& .MuiTableHead-root": {
      backgroundColor: theme.palette.grey[50],
    },
    "& .MuiTableCell-head": {
      fontWeight: "bold",
      color: theme.palette.primary.main,
    },
  }));

  const renderUserPrefHeaders = () => (
    <TableHead>
      <TableRow>
        <TableCell>Notify When</TableCell>
        <TableCell align="center">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
          >
            <EmailIcon fontSize="small" />
            <Typography variant="body2">Email</Typography>
          </Stack>
        </TableCell>
        <TableCell align="center">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
          >
            <SmsIcon fontSize="small" />
            <Typography variant="body2">Text</Typography>
          </Stack>
        </TableCell>
      </TableRow>
    </TableHead>
  );

  const renderAllNotificationsSwitch = () => {
    // Check if all email notifications are enabled
    const userKeys = Object.keys(userEmailPreferences) as (keyof NotificationPreferences)[];
    const hostKeys = Object.keys(hostEmailPreferences) as (keyof HostPreferences)[];
    
    const allUserEmailEnabled = userKeys.length > 0 && userKeys.every(key => userEmailPreferences[key]);
    const allHostEmailEnabled = hostKeys.length === 0 || hostKeys.every(key => hostEmailPreferences[key]);
    
    const allNotificationsEnabled = allUserEmailEnabled && allHostEmailEnabled;

    return (
      <TableRow key="AllUserPref">
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={1}>
            <NotificationsIcon color="error" />
            <Typography variant="body2" color="error" fontWeight="bold">
              All Notifications
            </Typography>
          </Stack>
        </TableCell>
        <TableCell align="center">
          <Switch
            checked={allNotificationsEnabled}
            onChange={(e) => handleAllEmailOpt(e.target.checked)}
            color="error"
          />
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        </TableCell>
      </TableRow>
    );
  };

  const renderUserPreferences = () => {
    const prefKeys = Object.keys(
      UserPrefMap,
    ) as (keyof NotificationPreferences)[];
    return (
      <TableBody>
        {renderAllNotificationsSwitch()}
        {prefKeys.map((prefKey) => (
          <TableRow key={prefKey}>
            <TableCell>
              <Typography variant="body2">{UserPrefMap[prefKey]}</Typography>
            </TableCell>
            <TableCell align="center">
              <Switch
                checked={userEmailPreferences[prefKey] || false}
                onChange={(e) => handleChange(prefKey, e.target.checked)}
                color="primary"
              />
            </TableCell>
            <TableCell align="center">
              {prefKey === "confirmationNeeded" ? (
                user.phoneNumber ? (
                  <Switch
                    checked={userTextPreferences[prefKey] || false}
                    onChange={(e) =>
                      handleTextChange(prefKey, e.target.checked)
                    }
                    color="primary"
                  />
                ) : (
                  <Chip
                    label="Phone Required"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )
              ) : (
                <ClearIcon color="disabled" />
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  };

  const renderHostPreferences = () => {
    const prefKeys = Object.keys(HostPrefMap) as (keyof HostPreferences)[];
    return (
      <TableBody>
        {prefKeys.map((prefKey) => (
          <TableRow key={prefKey}>
            <TableCell>
              <Typography variant="body2">{HostPrefMap[prefKey]}</Typography>
            </TableCell>
            <TableCell align="center">
              <Switch
                checked={hostEmailPreferences[prefKey] || false}
                onChange={(e) => handleHostChange(prefKey, e.target.checked)}
                color="primary"
              />
            </TableCell>
            <TableCell align="center">
              {prefKey === "playerConfirmed" ? (
                user?.phoneNumber ? (
                  <Switch
                    checked={hostTextPreferences[prefKey] || false}
                    onChange={(e) =>
                      handleHostTextChange(prefKey, e.target.checked)
                    }
                    color="primary"
                  />
                ) : (
                  <Chip
                    label="Phone Required"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )
              ) : (
                <ClearIcon color="disabled" />
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  };

  const SaveCancelBTN = () => {
    if (hasChanges()) {
      return (
        <Stack direction="row" spacing={2} justifyContent="center">
          <SaveIconBtn action={savePreferences} />
          <CancelIconBtn
            action={async () => {
              setSaveError(null);
              await resetPreferences();
            }}
          />
        </Stack>
      );
    }
    return null;
  };

  const renderPreferenceOptions = () => (
    <Stack spacing={3}>
      {saveError && (
        <Alert severity="error" onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      {Object.keys(userEmailPreferences).length > 0 && (
        <Paper elevation={2}>
          <StyledTableContainer>
            <Table>
              {renderUserPrefHeaders()}
              {renderUserPreferences()}
            </Table>
          </StyledTableContainer>
        </Paper>
      )}

      {Object.keys(hostEmailPreferences).length > 0 && (
        <Box>
          <Typography
            variant="h6"
            color="error"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <NotificationsIcon />
            Host Preferences
          </Typography>
          <Paper elevation={2}>
            <StyledTableContainer>
              <Table>
                {renderUserPrefHeaders()}
                {renderHostPreferences()}
              </Table>
            </StyledTableContainer>
          </Paper>
        </Box>
      )}

      <SaveCancelBTN />
    </Stack>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <NotificationsIcon color="primary" />
        Notification Preferences
      </Typography>

      <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: "grey.50" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body1" fontWeight="medium">Phone Number:</Typography>
          <UpdatePhoneNumber />
        </Box>
        {!user?.phoneNumber && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Add a phone number to receive SMS notifications for important
            updates.
          </Alert>
        )}
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        renderPreferenceOptions()
      )}
    </Box>
  );
}

export default UserPreferences;
